/**
 * Legacy Auth Adapter
 * 기존 AuthContext와 새로운 Auth Domain Layer 간의 어댑터
 * Adapter 패턴을 사용하여 호환성 유지
 */

import { LoginUseCase, LoginRequest, LoginResponse as DomainLoginResponse } from '../use-cases/LoginUseCase';
import { LogoutUseCase } from '../use-cases/LogoutUseCase';
import { CheckAuthUseCase } from '../use-cases/CheckAuthUseCase';
import { TokenService } from '../services/TokenService';
import { AuthRepositoryFactory } from '../repositories/AuthRepositoryFactory';
import { User } from '../models/User';
import { AuthUser } from '../stores/AuthStore';

// Legacy 타입과 호환되는 인터페이스
export interface LegacyLoginCredentials {
  username: string;
  password: string;
}

export interface LegacyLoginResponse {
  user: LegacyUser;
  access_token: string;
  refresh_token: string;
  token_type: string;
  session_id?: string;
}

export interface LegacyUser {
  id: string;
  name: string;
  user_number: string;
  phone: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  role?: string;
  org_id?: string;
  organization_id?: string;
  organization_name?: string;
  permissions?: string[];
  first_login?: boolean;
  temp_password?: boolean;
  member_number?: string;
  studyroom_name?: string;
  business_id?: string;
}

/**
 * Legacy Auth Adapter
 * 기존 AuthContext API를 새로운 DDD 구조로 위임
 */
export class LegacyAuthAdapter {
  private loginUseCase: LoginUseCase;
  private logoutUseCase: LogoutUseCase;
  private checkAuthUseCase: CheckAuthUseCase;
  private tokenService: TokenService;

  constructor() {
    this.loginUseCase = new LoginUseCase();
    this.logoutUseCase = new LogoutUseCase();
    this.checkAuthUseCase = new CheckAuthUseCase();
    this.tokenService = new TokenService(AuthRepositoryFactory.getInstance());
  }

  /**
   * Legacy 로그인 API를 새로운 LoginUseCase로 변환
   */
  async login(credentials: LegacyLoginCredentials): Promise<LegacyLoginResponse> {
    // Legacy 형태를 Domain 형태로 변환
    const domainRequest: LoginRequest = {
      email: credentials.username, // username을 email로 매핑
      password: credentials.password,
      deviceXCircle: {
        userAgent: navigator?.userAgent || 'Unknown',
        ipAddress: 'unknown', // 클라이언트에서는 실제 IP를 알기 어려움
      },
    };

    try {
      // Domain UseCase 실행
      const domainResponse = await this.loginUseCase.execute(domainRequest);

      if (!domainResponse.success) {
        throw new Error(domainResponse.error || 'Login failed');
      }

      // Domain 형태를 Legacy 형태로 변환
      return this.convertDomainToLegacyLoginResponse(domainResponse);
    } catch (error) {
      console.error('LegacyAuthAdapter.login 오류:', error);
      throw error;
    }
  }

  /**
   * Legacy 로그아웃 API를 새로운 LogoutUseCase로 변환
   */
  async logout(): Promise<void> {
    try {
      const result = await this.logoutUseCase.execute({
        reason: 'user_initiated',
      });

      if (!result.success) {
        console.warn('로그아웃 중 오류:', result.error);
        // Legacy 호환성을 위해 오류를 throw하지 않음
      }
    } catch (error) {
      console.error('LegacyAuthAdapter.logout 오류:', error);
      // Legacy 호환성을 위해 오류를 throw하지 않음
    }
  }

  /**
   * Legacy 인증 상태 확인을 새로운 CheckAuthUseCase로 변환
   */
  async checkAuthStatus(): Promise<boolean> {
    try {
      const result = await this.checkAuthUseCase.execute({ refreshIfNeeded: true });
      return result.isAuthenticated;
    } catch (error) {
      console.error('LegacyAuthAdapter.checkAuthStatus 오류:', error);
      return false;
    }
  }

  /**
   * Legacy 현재 사용자 정보 조회
   */
  async getCurrentUser(): Promise<LegacyUser> {
    try {
      const result = await this.checkAuthUseCase.execute({ refreshIfNeeded: false });
      
      if (!result.isAuthenticated || !result.user) {
        throw new Error('User not authenticated');
      }

      return this.convertDomainUserToLegacy(result.user);
    } catch (error) {
      console.error('LegacyAuthAdapter.getCurrentUser 오류:', error);
      throw error;
    }
  }

  /**
   * Legacy 토큰 갱신
   */
  async refreshAccessToken(): Promise<string> {
    try {
      const result = await this.checkAuthUseCase.refreshToken();
      
      if (!result.success) {
        throw new Error('Token refresh failed');
      }

      // TokenService에서 직접 accessToken 가져오기
      const currentToken = await this.tokenService.getCurrentToken();
      if (!currentToken) {
        throw new Error('No current token available');
      }

      return currentToken.accessToken;
    } catch (error) {
      console.error('LegacyAuthAdapter.refreshAccessToken 오류:', error);
      throw error;
    }
  }

  /**
   * Legacy 토큰 검증
   */
  async validateToken(): Promise<boolean> {
    try {
      const result = await this.checkAuthUseCase.execute({ refreshIfNeeded: false });
      return result.isAuthenticated;
    } catch (error) {
      console.error('LegacyAuthAdapter.validateToken 오류:', error);
      return false;
    }
  }

  /**
   * 현재 로그인 상태 확인
   */
  isLoggedIn(): boolean {
    return this.logoutUseCase.isLoggedIn();
  }

  /**
   * Domain LoginResponse를 Legacy LoginResponse로 변환
   */
  private convertDomainToLegacyLoginResponse(domainResponse: DomainLoginResponse): LegacyLoginResponse {
    if (!domainResponse.user || !domainResponse.token) {
      throw new Error('Invalid domain response');
    }

    return {
      user: this.convertDomainUserToLegacy(domainResponse.user),
      access_token: domainResponse.token.accessToken,
      refresh_token: domainResponse.token.refreshToken,
      token_type: 'Bearer',
      session_id: domainResponse.session?.id,
    };
  }

  /**
   * Domain User를 Legacy User로 변환
   */
  private convertDomainUserToLegacy(domainUser: AuthUser): LegacyUser {
    return {
      id: domainUser.id,
      name: domainUser.name,
      user_number: domainUser.email, // email을 user_number로 사용
      phone: '', // Domain User에는 phone이 없으므로 빈 값
      email: domainUser.email,
      is_active: domainUser.isActive,
      created_at: new Date().toISOString(), // Domain에서는 생성일이 없으므로 현재 시간
      updated_at: new Date().toISOString(), // Domain에서는 수정일이 없으므로 현재 시간
      role: domainUser.role,
      organization_id: domainUser.organization_id,
      organization_name: domainUser.organization_name,
      // 추가 Legacy 필드들은 undefined로 유지 (optional)
    };
  }

  /**
   * Legacy User를 Domain User로 변환 (필요시)
   */
  private convertLegacyToDomainUser(legacyUser: LegacyUser): AuthUser {
    return {
      id: legacyUser.id,
      email: legacyUser.email,
      name: legacyUser.name,
      role: legacyUser.role || 'user',
      isActive: legacyUser.is_active,
      profileImageUrl: undefined,
      lastLoginAt: undefined,
      accountLocked: false,
      passwordChangeRequired: false,
    };
  }
}

/**
 * Singleton 인스턴스
 * 전역에서 하나의 어댑터 인스턴스만 사용
 */
export const legacyAuthAdapter = new LegacyAuthAdapter();